import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, ShieldOff, UserX, UserCheck, Trash2, CreditCard, Activity, Phone as PhoneIcon, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD, shortDate, relativeTime } from "@/lib/dates";
import { cn } from "@/lib/utils";

const InfoRow = ({ label, value, mono = false }) => (
  <div className="flex items-center justify-between text-[13px] py-2 border-b border-border-default last:border-0">
    <span className="text-text-secondary">{label}</span>
    <span className={cn("text-white", mono && "jea-mono")}>{value ?? "—"}</span>
  </div>
);

const Section = ({ title, children, testid }) => (
  <div className="pb-4 mb-4 border-b border-border-default last:border-0 last:mb-0" data-testid={testid}>
    <div className="jea-caption mb-2">{title}</div>
    {children}
  </div>
);

const UserDetailDrawer = ({ userId, open, onClose, onAction, currentAdminId }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const isSelf = user?.id && currentAdminId && user.id === currentAdminId;

  useEffect(() => {
    if (!open || !userId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/admin/users/${userId}`);
        if (alive) setUser(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, open]);

  const suspended = user?.status === "suspended";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-[8px] z-[94]"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: 500 }} animate={{ x: 0 }} exit={{ x: 500 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            className="fixed top-0 right-0 h-screen w-[480px] max-w-[95vw] bg-bg-secondary border-l border-border-default z-[95] shadow-elevated overflow-y-auto"
            data-testid="admin-user-drawer"
          >
            <div className="sticky top-0 bg-bg-secondary z-10 border-b border-border-default px-6 py-4 flex items-start justify-between">
              <div className="min-w-0 flex-1">
                {loading || !user ? (
                  <Skeleton className="h-6 w-32" />
                ) : (
                  <>
                    <h2 className="text-white font-bold text-[18px] truncate" data-testid="drawer-user-name">
                      {user.full_name}
                    </h2>
                    <p className="text-text-secondary text-[13px] truncate">{user.email}</p>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-text-secondary hover:text-white"
                data-testid="admin-user-drawer-close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-6">
              {loading || !user ? (
                <div className="space-y-4">
                  {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
              ) : (
                <>
                  <Section title="Account Info" testid="drawer-account-info">
                    <InfoRow label="Role" value={user.role} />
                    <InfoRow label="Account Type" value={user.account_type} />
                    <InfoRow
                      label="Phone"
                      value={
                        user.phone ? (
                          <span className="inline-flex items-center gap-1.5" data-testid="drawer-phone-value">
                            <span className="jea-mono text-white">{user.phone}</span>
                            {user.phone_verified ? (
                              <CheckCircle2 size={13} className="text-success" aria-label="Verified">
                                <title>Verified</title>
                              </CheckCircle2>
                            ) : (
                              <Clock size={13} className="text-warning" aria-label="Pending">
                                <title>Pending verification</title>
                              </Clock>
                            )}
                          </span>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )
                      }
                    />
                    <InfoRow label="Status" value={user.status} />
                    <InfoRow label="Onboarded" value={user.onboarding_completed ? "Yes" : "No"} />
                    <InfoRow label="Joined" value={shortDate(user.created_at)} mono />
                    <InfoRow label="Updated" value={relativeTime(user.updated_at)} mono />
                  </Section>

                  <Section title="Subscription" testid="drawer-subscription">
                    {user.subscription ? (
                      <>
                        <InfoRow label="Plan" value={
                          user.subscription.plan_type === "pro"
                            ? `PRO ${user.subscription.path || ""}`
                            : `Standard · ${{ A: "3 Months", B: "6 Months", C: "12 Months" }[user.subscription.path] || user.subscription.path || "?"}`
                        } />
                        <InfoRow label="Judgment Count" value={user.subscription.debtor_count} />
                        <InfoRow label="Price" value={centsToUSD(user.subscription.price_cents || 0)} mono />
                        <InfoRow label="Status" value={user.subscription.status} />
                        <InfoRow label="Started" value={shortDate(user.subscription.started_at)} mono />
                        <InfoRow label="Renews" value={shortDate(user.subscription.renews_at)} mono />
                      </>
                    ) : (
                      <div className="text-text-muted text-[13px]">No subscription.</div>
                    )}
                  </Section>

                  <Section title="Usage" testid="drawer-usage">
                    <InfoRow label="Judgments" value={user.debtor_count} />
                    <InfoRow label="Queries Total" value={user.queries_total} />
                    <InfoRow label="Total Spend" value={centsToUSD(user.total_spend_cents || 0)} mono />
                  </Section>

                  <Section title="Recent Activity" testid="drawer-activity">
                    {(user.recent_activity || []).length === 0 ? (
                      <div className="text-text-muted text-[13px]">No recorded activity.</div>
                    ) : (
                      <div className="space-y-2">
                        {(user.recent_activity || []).map((a) => (
                          <div key={a.id} className="p-2 rounded-[6px] bg-bg-primary border border-border-default flex items-center gap-2 text-[12px]">
                            <Activity size={12} className="text-text-secondary shrink-0" />
                            <span className="text-white flex-1 truncate">{a.type}</span>
                            <span className="jea-date text-text-muted">{relativeTime(a.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Section>

                  <Section title="Danger Zone" testid="drawer-danger">
                    {isSelf ? (
                      <div className="text-text-muted text-[13px] py-2">
                        You cannot suspend, demote, or delete your own admin account.
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 mt-2">
                        {!user.is_admin && (
                          <button
                            type="button"
                            onClick={() => onAction?.(suspended ? "unsuspend" : "suspend", user)}
                            className={cn(
                              "h-11 rounded-[8px] border text-[14px] font-semibold transition-colors",
                              suspended
                                ? "border-success text-success hover:bg-success hover:text-[#091424]"
                                : "border-warning text-warning hover:bg-warning hover:text-[#091424]",
                            )}
                            data-testid="drawer-suspend-btn"
                          >
                            {suspended ? "Reactivate Account" : "Suspend Account"}
                          </button>
                        )}
                        {user.is_admin ? (
                          <button
                            type="button"
                            onClick={() => onAction?.("demote", user)}
                            className="h-11 rounded-[8px] border border-danger text-danger hover:bg-danger hover:text-white transition-colors text-[14px] font-semibold"
                            data-testid="drawer-demote-btn"
                          >
                            Demote Admin
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onAction?.("promote", user)}
                            className="h-11 rounded-[8px] border border-accent-pink text-accent-pink hover:bg-accent-pink hover:text-[#091424] transition-colors text-[14px] font-semibold"
                            data-testid="drawer-promote-btn"
                          >
                            Promote to Admin
                          </button>
                        )}
                        {!user.is_admin && (
                          <button
                            type="button"
                            onClick={() => onAction?.("delete", user)}
                            className="h-11 rounded-[8px] bg-danger text-white hover:bg-danger/90 transition-colors text-[14px] font-semibold"
                            data-testid="drawer-delete-btn"
                          >
                            Delete Account
                          </button>
                        )}
                      </div>
                    )}
                  </Section>
                </>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default UserDetailDrawer;
